/**
 * Lexserver REST API client — METADATA ONLY.
 *
 * This client calls lexserver for case registry operations (list, create, delete,
 * update metadata). Case CONTENT operations (documents, parties, deadlines,
 * timeline, files, search) go through the gateway WebSocket RPC (see gateway-rpc.ts).
 *
 * LexIDE must NEVER call lexserver for case content.
 */

import {
  getToken,
  getCurrentOrgId,
  getCurrentUserId,
  getCurrentScopes,
  clearAuthCache,
} from "@/lib/lexiun-jwt";
import { newTraceparent } from "@/lib/traceparent";

// Re-export for existing callers (OrgAdminLayout, useAuthIdentity) that
// import these from lexserver-client. New code should import from
// `@/lib/lexiun-jwt` directly.
export {
  getToken,
  getCurrentOrgId,
  getCurrentUserId,
  getCurrentScopes,
  clearAuthCache,
};

const LEXSERVER_BASE = "/api";

/** Build the common header set for an authenticated lexserver fetch.
 *  Injects a fresh W3C `traceparent` so lexserver's sentry-scope
 *  middleware extracts a stable trace_id for the request lifetime
 *  (otherwise the server mints a new orphan trace per inbound call). */
function authedHeaders(token: string): Record<string, string> {
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    traceparent: newTraceparent(),
  };
}

/** Make an authenticated GET request to lexserver. */
async function lexserverGet<T>(path: string): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${LEXSERVER_BASE}${path}`, {
    headers: authedHeaders(token),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: { message?: string } };
    // Carry the HTTP status like lexserverPost does — callers that must
    // distinguish "endpoint not there yet" (404) from a real failure need it
    // (first consumer: fetchClientDeleteCensus). Additive: still an Error.
    const err = new Error(body.error?.message ?? `lexserver ${res.status}`) as Error & {
      status: number;
    };
    err.status = res.status;
    throw err;
  }

  return res.json() as Promise<T>;
}

/** Make an authenticated POST request to lexserver. */
async function lexserverPost<T>(path: string, body: unknown): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${LEXSERVER_BASE}${path}`, {
    method: "POST",
    headers: authedHeaders(token),
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as { error?: { code?: string; message?: string } };
    const err = new Error(data.error?.message ?? `lexserver ${res.status}`) as Error & {
      status: number;
      code?: string;
    };
    err.status = res.status;
    err.code = data.error?.code;
    throw err;
  }

  return res.json() as Promise<T>;
}

/** Make an authenticated DELETE request to lexserver. */
async function lexserverDelete<T>(path: string): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${LEXSERVER_BASE}${path}`, {
    method: "DELETE",
    headers: authedHeaders(token),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as { error?: { message?: string } };
    throw new Error(data.error?.message ?? `lexserver ${res.status}`);
  }

  return res.json() as Promise<T>;
}

/** Make an authenticated PATCH request to lexserver. */
async function lexserverPatch<T>(path: string, body: unknown): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${LEXSERVER_BASE}${path}`, {
    method: "PATCH",
    headers: authedHeaders(token),
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as { error?: { code?: string; message?: string } };
    const err = new Error(data.error?.message ?? `lexserver ${res.status}`) as Error & {
      status: number;
      code?: string;
    };
    err.status = res.status;
    err.code = data.error?.code;
    throw err;
  }

  return res.json() as Promise<T>;
}

// ── Case API ────────────────────────────────────────────────────────────

/** Case registry entry from lexserver MongoDB (metadata only). */
export interface CaseListItem {
  caseId: string;
  /** The owning org (the registry row's `orgId`). NOTE: the wire field is `orgId`,
   *  not `ownerOrgId` (L10 fix — the old name was always undefined at runtime). */
  orgId: string;
  sharedOrgIds: string[];
  /** Parent ids — TRI-STATE (D-8/D-10, flat-storage deferred assignment):
   *  a non-empty STRING = assigned; explicit `null` = present-but-unassigned
   *  ("assignment-pending" — a valid, chattable case, NOT missing); `undefined`
   *  = absent only on legacy pre-topology rows. Treat null the same as undefined
   *  for "unassigned" UI, but never assume non-null. */
  clientId?: string | null;
  matterId?: string | null;
  matterType?: string;
  /** Flat-layout immutable path segment (entity-identity Slice 1); the on-disk
   *  folder is `{slug}--{uid}`. Optional/fail-soft — absent on legacy rows. */
  slug?: string;
  /** On-disk layout marker (D-4, never inferred). Absent on pre-migration rows. */
  storageShape?: "flat" | "legacy";
  title: string;
  status: string;
  createdBy: string;
  createdAt: string;
  /** Registry mutation time (create/rename/reassign/status). NOT user activity. */
  updatedAt: string;
  /** Last time the USER touched the case (open/chat/upload/edit) — the intended
   *  "most recent" sort key for the navigator (operator 2026-07-09). Optional +
   *  fail-soft: the navigator falls back to `updatedAt` until the backend tracks
   *  it on the case registry (relay flat-case-storage-lastactivity-relay-2026-07-09). */
  lastActivityAt?: string;
}

interface CaseListResponse {
  ok: boolean;
  data: CaseListItem[];
}

export async function fetchCases(): Promise<CaseListItem[]> {
  const result = await lexserverGet<CaseListResponse>("/cases");
  return result.data ?? [];
}

export async function fetchCase(caseId: string): Promise<CaseListItem | null> {
  try {
    const result = await lexserverGet<{ data: CaseListItem }>(`/cases/${caseId}`);
    return result.data ?? null;
  } catch {
    return null;
  }
}

// ── Case name-resolution (CCM Handoff #1 — OPTIONAL backend assist) ─────────

/**
 * One candidate from the server-side case-name resolver. The shape
 * mirrors a `CaseListItem` subset — enough to drive selection + the
 * disambiguation picker (parent ids, title, status).
 *
 * The CCM switch UI resolves names against the *local* `useCases`
 * list by default (works today, offline-friendly). When lexserver's
 * `GET /api/lx/resolve-case?q=` endpoint (Handoff #1) is live, the
 * switcher prefers it — it covers cases beyond the loaded list and
 * returns the authoritative disambiguation set. This client is the
 * thin, additive bridge to that endpoint.
 */
export interface ResolveCaseCandidate {
  caseId: string;
  clientId?: string;
  matterId?: string;
  title: string;
  status?: string;
}

export interface ResolveCaseResult {
  /** `unique` → exactly one match; `ambiguous` → ≥2 (caller MUST
   *  show the picker, never silent-guess); `none` → zero matches. */
  kind: "unique" | "ambiguous" | "none";
  candidates: ResolveCaseCandidate[];
}

/**
 * The server's wire shape (CCM Handoff #1 — `case-name-resolution.ts`):
 * discriminated on `status`, NOT this client's `kind`. `resolveCaseRemote`
 * adapts one to the other. The server's `case` / `candidates` carry the same
 * id + title fields `ResolveCaseCandidate` reads (a candidate also carries an
 * extra `hint` we ignore). A `resolved` (single match) response has NO
 * `candidates` field — the historical bug here was discarding it.
 */
type ServerResolveCaseResponse =
  | { status: "resolved"; case: ResolveCaseCandidate }
  | { status: "ambiguous"; candidates: ResolveCaseCandidate[] }
  | { status: "not_found" };

/**
 * OPTIONAL — resolve a typed case name server-side via
 * `GET /api/lx/resolve-case?q=`. Returns `null` when the endpoint
 * isn't deployed yet (Handoff #1 may not be live) OR on any transport
 * error, so callers transparently fall back to the local case list.
 * NEVER throws — the switch UI must keep working today.
 */
export async function resolveCaseRemote(
  q: string,
): Promise<ResolveCaseResult | null> {
  const trimmed = q.trim();
  if (!trimmed) return null;
  try {
    const token = await getToken();
    const res = await fetch(
      `${LEXSERVER_BASE}/lx/resolve-case?q=${encodeURIComponent(trimmed)}`,
      { headers: authedHeaders(token) },
    );
    // 404 (route absent) / 501 (not implemented) / any non-2xx →
    // signal "endpoint unavailable" so the caller falls back locally.
    if (!res.ok) return null;
    const body = (await res.json().catch(() => null)) as
      | { data?: ServerResolveCaseResponse }
      | ServerResolveCaseResponse
      | null;
    if (!body) return null;
    // Unwrap the lexserver `success` envelope ({ ok, data }) if present.
    const result = (
      "data" in body && body.data ? body.data : body
    ) as ServerResolveCaseResponse;
    // Adapt the server's `status` discriminator → this client's `kind`. A
    // single `resolved` match becomes a one-element candidate list (the bug
    // this replaces dropped it for lacking a `candidates` field); an
    // unrecognised envelope → null so the caller falls back to local matching.
    switch (result?.status) {
      case "resolved":
        return result.case ? { kind: "unique", candidates: [result.case] } : null;
      case "ambiguous":
        return Array.isArray(result.candidates)
          ? { kind: "ambiguous", candidates: result.candidates }
          : null;
      case "not_found":
        return { kind: "none", candidates: [] };
      default:
        return null;
    }
  } catch {
    return null;
  }
}

// ── Last-active-context (CCM Handoff #1, Surface A) ─────────────────────────

/**
 * The per-user last-active-context record (`GET /api/lx/context`). Only the
 * scope's own id-chain prefix is present; `updatedAt` is an ISO string (a
 * fresh user resolves to the synthesised epoch `global` default, not a row).
 */
export interface UserContextRecord {
  scope: "global" | "client" | "matter" | "case";
  clientId?: string;
  matterId?: string;
  caseId?: string;
  sessionKey: string;
  updatedAt: string;
  updatedFrom?: "lexide" | "im";
}

/**
 * The `PUT /api/lx/context` body. The server validates the chain strictly
 * (each scope carries EXACTLY its prefix ids), so build it via
 * `selectionToContextBody` (last-active-context.ts), never by hand.
 */
export interface PutContextBody {
  scope: "global" | "client" | "matter" | "case";
  clientId?: string;
  matterId?: string;
  caseId?: string;
  sessionKey: string;
  updatedFrom?: "lexide" | "im";
}

/**
 * GET the caller's last-active-context record. Returns `null` on any
 * transport error or a non-2xx (endpoint absent) — the resume path is
 * best-effort and must never block boot. NEVER throws.
 */
export async function getContext(): Promise<UserContextRecord | null> {
  try {
    const token = await getToken();
    const res = await fetch(`${LEXSERVER_BASE}/lx/context`, {
      headers: authedHeaders(token),
    });
    if (!res.ok) return null;
    const body = (await res.json().catch(() => null)) as
      | { data?: UserContextRecord }
      | UserContextRecord
      | null;
    if (!body) return null;
    return ("data" in body && body.data ? body.data : body) as UserContextRecord;
  } catch {
    return null;
  }
}

/**
 * PUT the caller's last-active-context. Fire-and-forget — best-effort, NEVER
 * throws into the UI (a failed persist just means the next selection change
 * re-PUTs). The body MUST be a valid scope/id-chain (see `selectionToContextBody`).
 */
export async function putContext(body: PutContextBody): Promise<void> {
  try {
    const token = await getToken();
    await fetch(`${LEXSERVER_BASE}/lx/context`, {
      method: "PUT",
      headers: authedHeaders(token),
      body: JSON.stringify(body),
    });
  } catch {
    // best-effort — swallow (the next selection change re-PUTs)
  }
}

// ── Client API (D5b) ────────────────────────────────────────────────────

import type { Client, Matter, ClientDeleteCensus } from "@lexiun/org-admin";
import type { DisplayNames } from "@/lib/entity-display";

/** Registry client entry — dates arrive as ISO strings. */
export type ClientListItem = Client;

/** Registry matter entry — dates arrive as ISO strings. Entity-identity Slice 1
 *  (2026-07-08) adds the optional identity tuple the registry now returns: the
 *  immutable path `slug`, the i18n `displayNames` (render `displayNames[locale]
 *  ?? name`), and the internal English `description`. Optional/fail-soft —
 *  legacy matters lack them until the §8 slug-as-path migration. */
export type MatterListItem = Matter & {
  slug?: string;
  displayNames?: DisplayNames;
  description?: string;
};

/** List every client under the caller's org. */
export async function fetchClients(): Promise<ClientListItem[]> {
  const orgId = await getCurrentOrgId();
  const result = await lexserverGet<{ data: ClientListItem[] }>(
    `/org/${orgId}/clients`,
  );
  return result.data ?? [];
}

/** Fetch a single client by id. Returns null on 404. */
export async function fetchClient(clientId: string): Promise<ClientListItem | null> {
  const orgId = await getCurrentOrgId();
  try {
    const result = await lexserverGet<{ data: ClientListItem }>(
      `/org/${orgId}/clients/${clientId}`,
    );
    return result.data ?? null;
  } catch {
    return null;
  }
}

/**
 * Delete census for the ClientDeleteConfirm flow (plan §2.4.1) — how many cases
 * reference the client (deleted-state UNFILTERED: recycle-binned cases count),
 * how many are legacy-`storageShape` (each one blocks), per-matter breakdown.
 *
 * Returns `null` ONLY for a 404 — the endpoint predates this SPA build or the
 * P1 lexserver half hasn't shipped yet — which the dialog renders as
 * "delete unavailable (backend pending)", NOT as a zero-case census. Any other
 * failure THROWS: a census we could not read must never present as "no cases,
 * safe to delete" (that is exactly the unknown-collapses-to-zero lie the
 * unknown-must-be-representable rule exists to prevent).
 */
export async function fetchClientDeleteCensus(
  clientId: string,
): Promise<ClientDeleteCensus | null> {
  const orgId = await getCurrentOrgId();
  try {
    const result = await lexserverGet<{ data: ClientDeleteCensus }>(
      `/org/${orgId}/clients/${clientId}/delete-census`,
    );
    return result.data;
  } catch (err) {
    if ((err as { status?: number }).status === 404) return null;
    throw err;
  }
}

// ── Matter API (D5b) ────────────────────────────────────────────────────

/** List every matter under a client. */
export async function fetchMatters(clientId: string): Promise<MatterListItem[]> {
  const orgId = await getCurrentOrgId();
  const result = await lexserverGet<{ data: MatterListItem[] }>(
    `/org/${orgId}/clients/${clientId}/matters`,
  );
  return result.data ?? [];
}

/* ---------- Matter vocabulary (platform-wide suggestion set — §7) ----------
 * The New-Case "Assunto" combobox reads this to suggest canonical matter names.
 * Contract: matter-vocabulary-platform-lexserver-handback-2026-07-07.md §3.
 * Rows are all PLATFORM rows + the caller-org's own org-private rows (org from
 * the JWT — no path scoping), pre-sorted `usageCount desc, name asc`. Platform
 * rows carry NULL creator ids (privacy hardening); org-private rows keep them
 * (only ever returned to that org) so the UI can rank same-user > same-org. */
export interface MatterVocabularyRow {
  name: string;
  matterType: string;
  visibility: "platform" | "org";
  createdByUserId: string | null;
  createdByOrgId: string | null;
  usageCount: number;
  /** Entity-identity Slice 1 (2026-07-08): the curated category `slug`, the i18n
   *  `displayNames` a matter inherits on a hit, and the internal English
   *  `description` (feeds the /resolve match). Optional/fail-soft. */
  slug?: string;
  displayNames?: DisplayNames;
  description?: string;
}

export async function fetchMatterVocabulary(): Promise<MatterVocabularyRow[]> {
  const result = await lexserverGet<{ data: { vocabulary: MatterVocabularyRow[] } }>(
    "/matter-vocabulary",
  );
  return result.data?.vocabulary ?? [];
}

/** Fetch a single matter by id. Returns null on 404. */
export async function fetchMatter(
  clientId: string,
  matterId: string,
): Promise<MatterListItem | null> {
  const orgId = await getCurrentOrgId();
  try {
    const result = await lexserverGet<{ data: MatterListItem }>(
      `/org/${orgId}/clients/${clientId}/matters/${matterId}`,
    );
    return result.data ?? null;
  } catch {
    return null;
  }
}

/**
 * List cases under a matter. Returns the full `CaseListItem` shape.
 *
 * NOTE: lexserver does not currently expose a nested
 * `GET /api/org/:orgId/clients/:c/matters/:m/cases` route — only the
 * flat `GET /api/cases` legacy endpoint (which returns every case in
 * the caller's org). This function hits the flat endpoint and filters
 * client-side. When lexserver gains a nested GET route (pre-D10 or in
 * a Phase B follow-up), swap the fetcher body; the signature and
 * contract remain unchanged.
 *
 * `clientId` is kept as a parameter so callers can safely pass it even
 * when the backing route doesn't use it yet — prevents a future
 * breaking-signature-change when the nested route lands.
 */
export async function fetchCasesByMatter(
  _clientId: string,
  matterId: string,
): Promise<CaseListItem[]> {
  const all = await fetchCases();
  return all.filter((c) => c.matterId === matterId);
}

// ── Assignments API (D9a) ───────────────────────────────────────────────
//
// The lexserver assignment routes return raw direct rows only; LexIDE
// composes cascade-derived rows client-side (by fetching parent-scope
// assignments and flagging them as `inherited`). D9a ships direct rows
// only; the cascade composition is a follow-up.

import type { UserAssignment, AssignmentRole } from "@lexiun/org-admin";

/**
 * Raw lexserver assignment row shape. Client + matter rows share
 * everything except which parent-id field is present. The generic
 * shape handles both; `mapAssignmentRow` keys off the requested
 * scope to populate `scopeId` correctly.
 */
interface RawAssignmentRow {
  assignmentId: string;
  clientId?: string;
  matterId?: string;
  orgId: string;
  userId: string;
  role: AssignmentRole;
  assignedBy: string;
  assignedAt: string;
  expiresAt?: string;
  isActive: boolean;
}

function mapAssignmentRow(
  row: RawAssignmentRow,
  scope: "client" | "matter",
): UserAssignment {
  const scopeId = scope === "client" ? row.clientId : row.matterId;
  if (!scopeId) {
    throw new Error(
      `lexserver assignment row missing ${scope}Id: ${JSON.stringify(row)}`,
    );
  }
  return {
    assignmentId: row.assignmentId,
    scope,
    scopeId,
    orgId: row.orgId,
    userId: row.userId,
    role: row.role,
    source: "direct",
    assignedBy: row.assignedBy,
    assignedAt: row.assignedAt,
    expiresAt: row.expiresAt,
    isActive: row.isActive,
  };
}

/**
 * Fetch direct client-level assignment rows. Errors propagate to
 * TanStack Query — callers render them via `query.error` rather
 * than silently getting an empty list.
 */
export async function fetchClientAssignments(
  clientId: string,
): Promise<UserAssignment[]> {
  const orgId = await getCurrentOrgId();
  const result = await lexserverGet<{ data: RawAssignmentRow[] }>(
    `/org/${orgId}/clients/${clientId}/assignments`,
  );
  return (result.data ?? []).map((row) => mapAssignmentRow(row, "client"));
}

/** Fetch direct matter-level assignment rows. Errors propagate. */
export async function fetchMatterAssignments(
  clientId: string,
  matterId: string,
): Promise<UserAssignment[]> {
  const orgId = await getCurrentOrgId();
  const result = await lexserverGet<{ data: RawAssignmentRow[] }>(
    `/org/${orgId}/clients/${clientId}/matters/${matterId}/assignments`,
  );
  return (result.data ?? []).map((row) => mapAssignmentRow(row, "matter"));
}

// ── Org runtime-config (Phase B.5 — LLM-keys pre-flight gate) ──────────────

/**
 * Masked runtime-config shape returned to non-PLATFORM_ADMIN callers.
 * Secret fields (`llm.*`, `security.*`) are returned as the
 * `***MASKED***` sentinel when present, `undefined` when absent.
 * Non-secret fields (`models`, `tools`, `agent`) pass through.
 *
 * Source of truth: `lexserver/src/routes/org-runtime-config.ts`
 * `maskSecrets()`.
 */
export interface OrgRuntimeConfigMasked {
  llm?: Record<string, string | undefined | null>;
  security?: Record<string, string | undefined | null>;
  models?: Record<string, unknown>;
  tools?: Record<string, unknown>;
  agent?: Record<string, unknown>;
  updatedAt?: string;
  updatedBy?: string;
}

/**
 * Fetch the caller-org's runtime config via
 * `GET /api/org/:orgId/runtime-config`. Auth is `requireOperator()` +
 * `requireOrgMatch()` server-side, so the JWT's `org` claim must
 * match the orgId we pass — we always read from `useAuthIdentity` /
 * `getCurrentOrgId` to keep that invariant.
 *
 * Used by `useOrgLLMKeysStatus` to drive the Phase B.5 chat gate.
 */
export async function fetchOrgRuntimeConfig(
  orgId: string,
): Promise<OrgRuntimeConfigMasked> {
  const result = await lexserverGet<{ data: OrgRuntimeConfigMasked }>(
    `/org/${encodeURIComponent(orgId)}/runtime-config`,
  );
  return result.data ?? {};
}

/**
 * The authoritative chat-default for the org, served by the lean
 * lexserver endpoint at `/api/org/:orgId/runtime-config/llm-status`. One
 * value per org, regardless of which container instance answers. LexIDE's
 * ModelBadge + Settings AI Engine consume this via `useLlmStatus`.
 *
 * `source` discriminates (mirrors lexserver/src/services/active-model.ts):
 *   - "org-chat-profile" ⇒ resolved through the platform `llmProfiles`
 *                          catalog from `runtimeConfig.models.chatProfile`
 *                          (or short-circuited by `chatProfileOverride`).
 *                          `chatProfile` carries the composite name
 *                          ("MED" / "MED:high") for tooltip display.
 *                          [Resolver-unification, §P5/§P10.]
 *   - "org-field"        ⇒ provider + model parsed from the legacy
 *                          `runtimeConfig.models.defaultModel`
 *                          (§P6.a additive window; dropped at §P6.b).
 *   - "missing"          ⇒ neither path is set; operator-actionable.
 *   - "unparseable"      ⇒ defensive; `raw` carries the bad value.
 */
export type OrgActiveModel =
  | {
      source: "org-chat-profile";
      provider: string;
      model: string;
      chatProfile: string;
      /** Effective `policy.thinkingDefault` from the resolver decision
       *  (base profile's policy OR composite-suffix overlay). The
       *  `<ModelBadge>` surfaces this alongside the model. Absent when
       *  the profile's policy carries no thinking default. */
      thinkingDefault?: string;
    }
  | { source: "org-field"; provider: string; model: string }
  | { source: "missing"; provider: null; model: null }
  | { source: "unparseable"; provider: null; model: null; raw: string };

/** P3 §8.6 — one row of the per-alias effective-rung diagnostic (mirrors the
 *  lexserver EffectiveTier). `platform` = delegated out to the platform
 *  catalog; `unavailable` = every configured rung is currently ineligible. */
export interface EffectiveTier {
  alias: string;
  tier: "primary" | "fallback" | "rescue" | "platform" | "unavailable";
  provider: string | null;
  model: string | null;
}

export interface OrgLlmStatus {
  orgId: string;
  activeModel: OrgActiveModel;
  /** Per-provider live health (P3). Absent-safe. */
  providerHealth?: Record<string, { status: "ok" | "degraded" | "down" }>;
  /** Per-alias effective ladder rung (P3 §8.6). Present only when the org has
   *  a DYNAMIC ALIASES table (absence invariant). */
  effectiveTiers?: EffectiveTier[];
}

export async function fetchOrgLlmStatus(orgId: string): Promise<OrgLlmStatus> {
  const result = await lexserverGet<{ data: OrgLlmStatus }>(
    `/org/${encodeURIComponent(orgId)}/runtime-config/llm-status`,
  );
  return result.data;
}

// ── Runtime version-skew (§C — LexIDE consumer of §B) ──────────────────────

/**
 * Mirrors the lexserver §B `GET /api/lx/version-skew` response (per-user;
 * `ai-docs/lexide-implementation-plan.md` § "Runtime version-skew").
 * `state` is the single source for the UPGRADE button:
 *   - `behind`        → newer image available; button ENABLED
 *   - `current`       → up to date; button grey/disabled
 *   - `indeterminate` → running/target version "unknown"; disabled+tooltip
 *   - `unreachable`   → proxy/probe failed (e.g. pre-94bd2a32 image);
 *                       disabled+tooltip — NEVER a false "up to date"
 */
export interface RuntimeVersionSkew {
  state: "current" | "behind" | "indeterminate" | "unreachable";
  running: {
    version: string;
    commit: string;
    builtAt: string;
    openclawVersion: string;
  } | null;
  target: { targetVersion: string; targetCommit: string; targetBuiltAt: string };
}

/** Fetch the caller-runtime's version-skew (§B). Throws on transport error
 *  (caller treats a throw as "unavailable" — never a silent up-to-date). */
export async function fetchRuntimeVersionSkew(): Promise<RuntimeVersionSkew> {
  const r = await lexserverGet<{ data: RuntimeVersionSkew }>("/lx/version-skew");
  return r.data;
}

/**
 * Trigger the T2 user-scoped upgrade (graceful single-container restart
 * onto the platform-target image). The endpoint is OWNED BY THE
 * runtime-restart track (`lexruntime-implementation-plan.md`; reuses the
 * `7370a94a` orchestrator primitive — NOT a second restart mechanism) and
 * may not be deployed yet — callers MUST handle a rejection fail-soft
 * (toast), never crash. Scoped to the caller's own runtime via the Lexiun
 * JWT; no platform-admin path is ever called from the end-user UI.
 */
export async function triggerRuntimeUpgrade(): Promise<void> {
  await lexserverPost<{ data?: unknown }>("/lx/upgrade", {});
}

// ── Feedback API (Phase 10.A + 10.C + 10.D) ────────────────────────────────

import type {
  CreateFeedbackInput,
  CreatedFeedback,
  FeedbackListParams,
  FeedbackListResponse as OrgFeedbackListResponse,
  FeedbackRecord,
  TriageFeedbackInput,
} from "@/types/feedback";

interface FeedbackPostResponse {
  ok: true;
  data: CreatedFeedback;
}

interface MyFeedbackListResponse {
  ok: true;
  data: Array<{
    feedbackId: string;
    polarity: "positive" | "negative" | "neutral";
    comment?: string;
    surface: string;
    surfaceContext: Record<string, unknown>;
    clientMeta: Record<string, unknown>;
    status: string;
    createdAt: string;
    updatedAt: string;
  }>;
  meta: { pagination?: { hasMore?: boolean; nextCursor?: string | null } };
}

interface OrgFeedbackEnvelope {
  ok: true;
  data: FeedbackRecord[];
  meta: { pagination?: { hasMore?: boolean; nextCursor?: string | null } };
}

/**
 * Submit a feedback record.
 *
 * Surfaces the server's error code on the thrown Error so callers can
 * branch on `ALREADY_EXISTS` (409 dedup) vs `VALIDATION_ERROR` (400).
 */
export async function submitFeedback(input: CreateFeedbackInput): Promise<CreatedFeedback> {
  const result = await lexserverPost<FeedbackPostResponse>("/feedback", input);
  return result.data;
}

/** List the caller's own feedback (paginated). */
export async function listMyFeedback(query?: {
  limit?: number;
  cursor?: string;
  surface?: string;
}): Promise<MyFeedbackListResponse> {
  const qs = new URLSearchParams();
  if (query?.limit) qs.set("limit", String(query.limit));
  if (query?.cursor) qs.set("cursor", query.cursor);
  if (query?.surface) qs.set("surface", query.surface);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return lexserverGet<MyFeedbackListResponse>(`/feedback/mine${suffix}`);
}

/** Delete one of the caller's own feedback records. */
export async function deleteMyFeedback(feedbackId: string): Promise<{ ok: boolean }> {
  return lexserverDelete<{ ok: boolean }>(`/feedback/${encodeURIComponent(feedbackId)}`);
}

/**
 * Phase 10.C — list feedback for the caller's org (admin browser).
 *
 * Auth: requires `operator.admin` (or `platform.admin`); the lexserver
 * route enforces `requireOperator + requireOrgMatch + requireOrgAdmin`.
 * Read-only in Phase C; PATCH ships in Phase D (status + triageNotes).
 */
export async function listOrgFeedback(
  params: FeedbackListParams = {},
): Promise<OrgFeedbackListResponse> {
  const orgId = await getCurrentOrgId();
  const qs = new URLSearchParams();
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.surface) qs.set("surface", params.surface);
  if (params.polarity) qs.set("polarity", params.polarity);
  if (params.status) qs.set("status", params.status);
  if (params.category) qs.set("category", params.category);
  if (params.q) qs.set("q", params.q);
  if (params.fromDate) qs.set("fromDate", params.fromDate);
  if (params.toDate) qs.set("toDate", params.toDate);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const env = await lexserverGet<OrgFeedbackEnvelope>(
    `/org/${orgId}/feedback${suffix}`,
  );
  return {
    data: env.data,
    pagination: {
      hasMore: env.meta.pagination?.hasMore ?? false,
      nextCursor: env.meta.pagination?.nextCursor ?? undefined,
    },
  };
}

/**
 * Phase 10.D — apply a triage update to a single feedback record.
 *
 * Auth: requires `operator.admin` (or `platform.admin`); same gates as
 * `listOrgFeedback`. Server enforces "at least one of status / triageNotes
 * must be present" via Zod refinement, so we don't pre-validate here.
 *
 * `triagedBy` is set server-side from the JWT `sub` — never user-supplied,
 * so we can't (and don't) pass it.
 */
export async function patchOrgFeedback(
  feedbackId: string,
  body: TriageFeedbackInput,
): Promise<FeedbackRecord> {
  const orgId = await getCurrentOrgId();
  const env = await lexserverPatch<{ ok: true; data: FeedbackRecord }>(
    `/org/${orgId}/feedback/${encodeURIComponent(feedbackId)}`,
    body,
  );
  return env.data;
}
